// @ts-nocheck

import { pipe, share, merge, makeSubject, filter, onPush } from "wonka";

import type {
  Operation,
  OperationResult,
  Exchange,
  ExchangeIO,
  CombinedError,
  RequestPolicy
} from "@urql/core";
import { stringifyDocument, createRequest, makeOperation } from "@urql/core";

import type {
  SerializedRequest,
  CacheExchangeOpts,
  StorageAdapter
} from "./types";
import { cacheExchange } from "./cacheExchange";
import { toRequestPolicy } from "./helpers/operation";
import { addCacheBreadcrumb, captureException } from "../../../config/sentry";

const policyLevel = {
  "cache-only": 0,
  "cache-first": 1,
  "network-only": 2,
  "cache-and-network": 3
} as const;

/** Input parameters for the {@link offlineExchange}.
 * @remarks
 * This configuration object extends the {@link CacheExchangeOpts}
 * as the `offlineExchange` extends the regular {@link cacheExchange}.
 */
export interface OfflineExchangeOpts extends CacheExchangeOpts {
  /** Configures an offline storage adapter for Graphcache.
   *
   * @remarks
   * A {@link StorageAdapter} allows Graphcache to write data to an external,
   * asynchronous storage, and hydrate data from it when it first loads.
   * This allows you to preserve normalized data between restarts/reloads.
   *
   * @see {@link https://urql.dev/goto/docs/graphcache/offline} for the full Offline Support docs.
   */
  storage: StorageAdapter;
  /** Predicate function to determine whether a {@link CombinedError} hints at a network error.
   *
   * @remarks
   * Not ever {@link CombinedError} means that the device is offline and by default
   * the `offlineExchange` will check for common network error messages and check
   * `navigator.onLine`. However, when `isOfflineError` is passed it can replace
   * the default offline detection.
   */
  isOfflineError?(
    error: undefined | CombinedError,
    result: OperationResult
  ): boolean;
}

const DEBUG_OFFLINE_EXCHANGE =
  (typeof window !== 'undefined' && (window as any).__DEBUG_OFFLINE__ === true) ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_OFFLINE_EXCHANGE') === '1');



/** Exchange factory that creates a normalized cache exchange in Offline Support mode.
 *
 * @param opts - A {@link OfflineExchangeOpts} configuration object.
 * @returns the created normalized, offline cache {@link Exchange}.
 *
 * @remarks
 * The `offlineExchange` is a wrapper around the regular {@link cacheExchange}
 * which adds logic via the {@link OfflineExchangeOpts.storage} adapter to
 * recognize when it's offline, when to retry failed mutations, and how
 * to handle longer periods of being offline.
 *
 * @see {@link https://urql.dev/goto/docs/graphcache/offline} for the full Offline Support docs.
 */
export const offlineExchange =
  <C extends OfflineExchangeOpts>(opts: C): Exchange =>
    (input) => {
      const { storage } = opts;
      const isOfflineError =
        opts.isOfflineError ||
        ((error: undefined | CombinedError) =>
          error &&
          error.networkError &&
          !error.response &&
          ((typeof navigator !== "undefined" && navigator.onLine === false) ||
            /request failed|failed to fetch|network\s?error/i.test(
              error.networkError.message
            )));

      if (
        storage &&
        storage.onOnline &&
        storage.readMetadata &&
        storage.writeMetadata
      ) {
        const { forward: outerForward, client, dispatchDebug } = input;
        const { source: reboundOps$, next } = makeSubject<Operation>();

        // CUSTOM CLOVIS CODE
        // Use existing queue if available, otherwise create new one
        // This prevents losing operations when exchange is recreated
        let failedQueue: Operation[] =
          (typeof window !== "undefined" && window.__offlineFailedQueue) || [];

        function initGlobalFailedQueue() {
          // Ensure the global reference is always up-to-date
          if (typeof window !== "undefined") {
            // @ts-ignore
            window.__offlineFailedQueue = failedQueue;
          }
        }

        initGlobalFailedQueue();

        let hasRehydrated = false;
        let isFlushingQueue = false;

        const updateMetadata = () => {
          if (hasRehydrated) {
            const requests: SerializedRequest[] = [];
            for (let i = 0; i < failedQueue.length; i++) {
              const operation = failedQueue[i];
              if (operation.kind === "mutation") {
                requests.push({
                  query: stringifyDocument(operation.query),
                  variables: operation.variables,
                  extensions: operation.extensions
                });
              }
            }
            storage.writeMetadata!(requests);
          }
        };

        const filterQueue = (key: number) => {
          for (let i = failedQueue.length - 1; i >= 0; i--)
            if (failedQueue[i].key === key) failedQueue.splice(i, 1);
        };

        const flushQueue = () => {
          if (DEBUG_OFFLINE_EXCHANGE) {
            console.log("[offlineExchange] flushQueue called", {
              queueLength: failedQueue.length,
              isFlushingQueue,
              operations: failedQueue.map((op) => ({
                kind: op.kind,
                key: op.key,
                operationName: op.query.definitions[0]?.name?.value
              }))
            });
          }


          if (!isFlushingQueue && failedQueue.length > 0) {
            // Sentry breadcrumb for queue flush
            addCacheBreadcrumb({
              category: "cache.offline_queue",
              message: "Flushing offline queue",
              data: {
                queueLength: failedQueue.length,
                operationTypes: failedQueue.map(op => op.query.definitions[0]?.name?.value)
              }
            });

            const sent = new Set<number>();
            isFlushingQueue = true;
            for (let i = 0; i < failedQueue.length; i++) {
              const operation = failedQueue[i];
              if (DEBUG_OFFLINE_EXCHANGE) {
                console.log("[offlineExchange] Processing failed operation", {
                  kind: operation.kind,
                  key: operation.key,
                  operationName: operation.query.definitions[0]?.name?.value,
                  alreadySent: sent.has(operation.key)
                });
              }


              if (operation.kind === "mutation" || !sent.has(operation.key)) {
                sent.add(operation.key);
                if (operation.kind !== "subscription") {
                  next(makeOperation("teardown", operation));
                  let overridePolicy: RequestPolicy = "cache-first";
                  for (let i = 0; i < failedQueue.length; i++) {
                    const { requestPolicy } = failedQueue[i].context;
                    if (policyLevel[requestPolicy] > policyLevel[overridePolicy])
                      overridePolicy = requestPolicy;
                  }
                  next(toRequestPolicy(operation, overridePolicy));
                } else {
                  next(toRequestPolicy(operation, "cache-first"));
                }
              }
            }
            isFlushingQueue = false;
            failedQueue.length = 0;
            updateMetadata();
            if (DEBUG_OFFLINE_EXCHANGE) {
              console.log("[offlineExchange]: flushQueue completed, queue cleared");
            }
          } else if (failedQueue.length === 0) {
            if (DEBUG_OFFLINE_EXCHANGE) {
              console.log("[offlineExchange]: flushQueue skipped - queue is empty");

            }
          }
        };

        const forward: ExchangeIO = (ops$) => {
          return pipe(
            outerForward(ops$),
            filter((res) => {
              const operationName =
                res.operation.query.definitions[0]?.name?.value;

              if (
                hasRehydrated &&
                res.operation.kind === "mutation" &&
                res.operation.context.optimistic &&
                isOfflineError(res.error, res)
              ) {
                // CUSTOM CLOVIS CODE
                /**
                 * This is covering the flow where we can update a task that was created offline while being offline.
                 * Flow:
                 * - Go offline -> Create task -> Update task -> Go online
                 *
                 * If this flow happens, we wouldn't like to have the updateTask in the failedQueue, but instead we are "updating" the request body (the input) of the createTask.
                 */

                const operationName =
                  res.operation.query.definitions[0]?.name?.value;
                const isUpdateTask = operationName === "updateTask";

                const hasInputId =
                  res.operation.variables &&
                  res.operation.variables.input &&
                  typeof res.operation.variables.input.id === "string";

                const isUpdatingTemporaryTask =
                  hasInputId &&
                  res.operation.variables.input.id.startsWith("temp_");

                if (isUpdateTask && isUpdatingTemporaryTask) {
                  let foundCreateTask = false;
                  const tempId = res.operation.variables?.input?.id;

                  failedQueue = failedQueue.map((operation) => {
                    const opName = operation.query.definitions[0]?.name?.value;
                    const opTempId =
                      operation.variables?.input?.temporary_task_id;

                    if (opName === "createTask" && opTempId === tempId) {
                      foundCreateTask = true;

                      const mergedOperation = {
                        ...operation,
                        variables: {
                          ...operation.variables,
                          input: {
                            ...operation.variables.input,
                            description:
                              res.operation.variables.input.description,
                            end_date: res.operation.variables.input.end_date,
                            start_date: res.operation.variables.input.start_date
                          }
                        }
                      };

                      return mergedOperation;
                    }
                    return operation;
                  });

                  initGlobalFailedQueue();

                  if (foundCreateTask) {
                    updateMetadata();
                  }
                  return false;
                }

                if (DEBUG_OFFLINE_EXCHANGE) {
                  console.log(
                    "[offlineExchange]: Adding failed mutation to queue",
                    {
                      operation: res.operation
                    }
                  );
                }

                // Sentry breadcrumb for failed mutation (always record - important for debugging)
                addCacheBreadcrumb({
                  category: "cache.offline_queue",
                  message: "Failed mutation added to offline queue",
                  data: {
                    operationName,
                    errorMessage: res.error?.message,
                    isNetworkError: !!res.error?.networkError
                  },
                  isError: true
                });

                failedQueue.push(res.operation);
                updateMetadata();

                return false;
              } else {
                if (DEBUG_OFFLINE_EXCHANGE) {
                  console.log(
                    "[offlineExchange]: operation result passed",
                    res.operation
                  );
                }
              }

              return true;
            }),
            share
          );
        };

        const cacheResults$ = cacheExchange({
          ...opts,
          storage: {
            ...storage,
            readData() {
              const hydrate = storage.readData();
              return {
                async then(onEntries) {
                  const mutations = await storage.readMetadata!();
                  for (let i = 0; mutations && i < mutations.length; i++) {
                    failedQueue.push(
                      client.createRequestOperation(
                        "mutation",
                        createRequest(mutations[i].query, mutations[i].variables),
                        mutations[i].extensions
                      )
                    );
                  }
                  onEntries!(await hydrate);
                  if (DEBUG_OFFLINE_EXCHANGE) {
                    console.log(
                      "[offlineExchange]: Setting up storage.onOnline callback"
                    );
                  }

                  storage.onOnline!(() => {
                    if (DEBUG_OFFLINE_EXCHANGE) {
                      console.log(
                        "[offlineExchange]: storage.onOnline callback triggered"
                      );
                    }

                    // Sentry breadcrumb for online transition
                    addCacheBreadcrumb({
                      category: "cache.online_transition",
                      message: "Device came online, flushing queue",
                      data: { queueLength: failedQueue.length }
                    });

                    flushQueue();
                  });
                  hasRehydrated = true;

                  // Sentry breadcrumb for successful rehydration
                  addCacheBreadcrumb({
                    category: "cache.rehydration",
                    message: "Cache rehydration completed",
                    data: {
                      pendingMutations: failedQueue.filter(op => op.kind === "mutation").length,
                      pendingQueries: failedQueue.filter(op => op.kind === "query").length
                    }
                  });

                  if (typeof window !== "undefined") {
                    // @ts-ignore
                    window.__hasCacheRehydrated = true;
                  }
                  flushQueue();
                }
              };
            }
          }
        })({
          client,
          dispatchDebug,
          forward
        });

        return (operations$) => {
          const opsAndRebound$ = merge([
            reboundOps$,
            pipe(
              operations$,
              onPush((operation) => {
                if (operation.kind === "query" && !hasRehydrated) {
                  failedQueue.push(operation);
                } else if (operation.kind === "teardown") {
                  filterQueue(operation.key);
                }
              })
            )
          ]);

          return pipe(
            cacheResults$(opsAndRebound$),
            filter((res) => {
              if (res.operation.kind === "query") {
                if (isOfflineError(res.error, res)) {
                  next(toRequestPolicy(res.operation, "cache-only"));
                  failedQueue.push(res.operation);
                  return false;
                } else if (!hasRehydrated) {
                  filterQueue(res.operation.key);
                }
              }
              return true;
            })
          );
        };
      }

      return cacheExchange(opts)(input);
    };
